import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import stili from '../../components/competenze/stili';
import { TIPI_SOGGETTO } from './comuni';

const CAMPI_VUOTI = {
  ragione_sociale: '', tipo_soggetto: '', codice_fiscale: '', partita_iva: '', codice_azienda: '', codice_ateco: '',
  indirizzo: '', cap: '', comune: '', provincia: '', pec: '', email: '', telefono: '', note: '',
};

// Creazione e modifica di un'azienda. Prima di salvare controlla (senza bloccare): avvisi formali, identificativo già usato
// (blocca, con link alla scheda), "forse è…" per nome simile, aziende eliminate con lo stesso identificativo.
export default function FormAnagrafica({ anagrafica, onChiudi, onSalvato, onRicarica }) {
  const [form, setForm] = useState(() => (anagrafica
    ? Object.fromEntries(Object.keys(CAMPI_VUOTI).map(c => [c, anagrafica[c] ?? '']))
    : { ...CAMPI_VUOTI }));
  const [controllo, setControllo] = useState(null);
  const [errore, setErrore] = useState('');
  const [conflitto, setConflitto] = useState(null);
  const [esistenteServer, setEsistenteServer] = useState(null);
  const [salvataggio, setSalvataggio] = useState(false);

  // Controllo con un piccolo ritardo mentre si scrive
  useEffect(() => {
    if (!form.ragione_sociale.trim() && !form.codice_fiscale.trim() && !form.partita_iva.trim()) { setControllo(null); return undefined; }
    let annullato = false;
    const t = setTimeout(() => {
      api.get('/anagrafiche/controlla', { params: {
        nome: form.ragione_sociale, codice_fiscale: form.codice_fiscale, partita_iva: form.partita_iva, tipo_soggetto: form.tipo_soggetto || undefined, escludi_id: anagrafica?.id,
      } }).then(r => { if (!annullato) setControllo(r.data); }).catch(() => {});
    }, 350);
    return () => { annullato = true; clearTimeout(t); };
  }, [form.ragione_sociale, form.codice_fiscale, form.partita_iva, form.tipo_soggetto, anagrafica?.id]);

  const personaFisica = form.tipo_soggetto === 'persona_fisica';   // un privato: «Cognome e nome», codice fiscale obbligatorio, nessuna partita IVA
  const imposta = (campo, valore) => { setForm(f => ({ ...f, [campo]: valore })); setEsistenteServer(null); };
  const esistente = esistenteServer || controllo?.identificativo_esistente?.[0] || null;

  const salva = async (e) => {
    e.preventDefault();
    setErrore('');
    setSalvataggio(true);
    try {
      const corpo = { ...form, tipo_soggetto: form.tipo_soggetto || null };
      const res = anagrafica
        ? await api.put(`/anagrafiche/${anagrafica.id}`, { ...corpo, version: anagrafica.version })
        : await api.post('/anagrafiche', corpo);
      onSalvato(res.data);
    } catch (err) {
      const d = err.response?.data;
      if (err.response?.status === 409 && d?.codice === 'CONFLITTO_VERSIONE') setConflitto(d);
      else if (err.response?.status === 409 && d?.codice === 'IDENTIFICATIVO_ESISTENTE') setEsistenteServer(d.esistente);
      else setErrore(d?.errore || 'Errore nel salvataggio');
    } finally { setSalvataggio(false); }
  };

  const campo = (nome, etichetta, extra = {}) => (
    <div style={{ ...stili.campo, flex: extra.larghezza || '1 1 200px', minWidth: 0 }}>
      <label style={stili.label} htmlFor={`an-${nome}`}>{etichetta}</label>
      <input id={`an-${nome}`} value={form[nome]} onChange={e => imposta(nome, e.target.value)} style={stili.input} {...extra.attr} />
    </div>
  );
  const avvisi = controllo?.avvisi || [];
  // Solo avviso: vale per le anagrafiche collegate a una scheda di Scadenziario/Parcellazione (da lì nasce il nome della cartella)
  const collegata = (anagrafica?.servizi?.parcellazione_scadenziario || []).length > 0;
  const avvisoCartella = collegata && String(form.codice_azienda || '').trim() !== String(anagrafica.codice_azienda || '').trim();

  return (
    <div style={stili.overlay} role="dialog" aria-label={anagrafica ? 'Modifica azienda' : 'Nuova azienda'}>
      <div style={{ ...stili.modal, maxWidth: '760px' }}>
        <h3 style={{ marginTop: 0 }}>{anagrafica ? 'Modifica azienda' : 'Nuova azienda'}</h3>
        <form onSubmit={salva}>
          {conflitto && (
            <div role="alert" style={{ ...stili.errore, border: '1px solid #fecaca' }}>
              <strong>{conflitto.errore}</strong>: è stata modificata da un altro operatore. Ricarica per vedere i dati aggiornati; le tue modifiche non salvate andranno perse.
              <div style={{ marginTop: '0.5rem' }}><button type="button" style={stili.btn} onClick={onRicarica}>Ricarica</button></div>
            </div>
          )}
          {errore && <div style={stili.errore}>{errore}</div>}
          {esistente && (
            <div role="alert" style={{ ...stili.errore, border: '1px solid #fecaca' }}>
              Esiste già: <Link to={`/anagrafiche/${esistente.id}`} onClick={onChiudi}>{esistente.ragione_sociale}</Link> (stesso codice fiscale o partita IVA). Apri la sua scheda invece di crearne un'altra.
            </div>
          )}
          <div style={{ ...stili.row, gap: '0.8rem' }}>
            {campo('ragione_sociale', personaFisica ? 'Cognome e nome' : 'Ragione sociale', { larghezza: '2 1 320px', attr: { required: true, autoFocus: true } })}
            <div style={{ ...stili.campo, flex: '1 1 200px' }}>
              <label style={stili.label} htmlFor="an-tipo_soggetto">Tipo di soggetto</label>
              <select id="an-tipo_soggetto" value={form.tipo_soggetto} onChange={e => imposta('tipo_soggetto', e.target.value)} style={stili.input}>
                <option value="">Non indicato</option>
                {Object.entries(TIPI_SOGGETTO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div style={{ ...stili.row, gap: '0.8rem' }}>
            {campo('codice_fiscale', personaFisica ? 'Codice fiscale (obbligatorio)' : 'Codice fiscale', { attr: { maxLength: 16 } })}
            {(!personaFisica || form.partita_iva) && campo('partita_iva', 'Partita IVA', { attr: { maxLength: 20 } })}
            {campo('codice_azienda', 'Codice azienda (paghe e contabilità)', { attr: { maxLength: 20 } })}
          </div>
          {avvisoCartella && (
            <div role="status" aria-label="Avviso codice azienda" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.6rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.86rem' }}>
              Cambiando il codice azienda cambia il nome della cartella dell'Excel di controllo di Scadenziario: le vecchie cartelle restano da eliminare a mano.
            </div>
          )}
          {avvisi.length > 0 && (
            <ul aria-label="Avvisi" style={{ margin: '0 0 0.8rem', paddingLeft: '1.2rem', color: '#9a3412', fontSize: '0.84rem' }}>
              {avvisi.map(a => <li key={a.codice + a.campo}>{a.messaggio}</li>)}
            </ul>
          )}
          {(controllo?.simili || []).length > 0 && !esistente && (
            <div role="status" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.6rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.86rem' }}>
              <strong>Forse è:</strong>{' '}
              {controllo.simili.slice(0, 5).map((s, i) => (
                <span key={s.id}>{i > 0 && ', '}<Link to={`/anagrafiche/${s.id}`} target="_blank" rel="noreferrer">{s.ragione_sociale}</Link>{s.somiglianza === 'uguale' ? ' (stesso nome)' : ''}</span>
              ))}
              . Se è la stessa azienda apri la sua scheda; altrimenti puoi salvare comunque.
            </div>
          )}
          {(controllo?.eliminate || []).length > 0 && (
            <div role="status" style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.6rem 0.8rem', marginBottom: '0.8rem', fontSize: '0.86rem' }}>
              Nel Cestino c'è già un'azienda con questo codice: <Link to="/anagrafiche/cestino" onClick={onChiudi}>ripristinala dal Cestino</Link> invece di crearne un'altra.
            </div>
          )}
          <div style={{ ...stili.row, gap: '0.8rem' }}>
            {campo('indirizzo', 'Indirizzo', { larghezza: '2 1 280px' })}
            {campo('cap', 'CAP', { larghezza: '0 1 100px', attr: { maxLength: 10 } })}
            {campo('comune', 'Comune')}
            {campo('provincia', 'Prov.', { larghezza: '0 1 80px', attr: { maxLength: 5 } })}
          </div>
          <div style={{ ...stili.row, gap: '0.8rem' }}>
            {campo('pec', 'PEC')}
            {campo('email', 'Email')}
            {campo('telefono', 'Telefono', { larghezza: '1 1 150px' })}
            {campo('codice_ateco', 'Codice ATECO', { larghezza: '0 1 130px', attr: { maxLength: 10 } })}
          </div>
          <div style={stili.campo}>
            <label style={stili.label} htmlFor="an-note">Note</label>
            <textarea id="an-note" value={form.note} onChange={e => imposta('note', e.target.value)} rows={2} style={{ ...stili.input, fontFamily: 'inherit' }} />
          </div>
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" disabled={salvataggio || Boolean(esistente) || !form.ragione_sociale.trim()} style={{ ...stili.btn, opacity: salvataggio || esistente ? 0.5 : 1 }}>
              {salvataggio ? 'Salvataggio…' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
